import styled from '@emotion/styled'
import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, SiteMetricsByDim, SiteMetricsByTime } from '@pubstack/common/src/analytics/query'
import { useAtom } from 'jotai'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useBreakdownTabs } from '~/hooks/useBreakdownTabs'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { AnalyticsPage } from '~/modules/analytics/AnalyticsPage'
import { useBuildContextWithFilterEnforced } from '~/modules/analytics/useContext'
import { contextState } from '~/state'
import { onBreakdownRowClick } from '~/utils/analytics'
import { PureSiteBreakdown } from './PureSiteBreakdown'
import { PureSiteChartsContainer } from './PureSiteChartsContainer'
import { SiteInventoryTabLabel } from './PureSiteInventoryChart'

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  & > * + * {
    margin-top: 20px;
  }
`

const FILTER_DIMENSIONS: Dimension[] = ['bidder', 'adUnit', 'device', 'country', 'size', 'pubstackRefresh', 'abTestPopulation', 'stackIdVersion']
const QUERY_DIMENSIONS: Dimension[] = [...FILTER_DIMENSIONS, 'tagId']
const DASHBOARD: AnalyticsQueryDashboard = { name: 'sites', filterType: 'auctions' }

export const SiteDetailsPage: FunctionComponent = () => {
  const search = useSearchParams()
  const searchParams = JSON.parse(atob(search[0].get('mapping') ?? "{site: '', siteName:''}"))
  const tagId = searchParams.site
  const [context, setContext] = useAtom(contextState)
  const [breakdownDimension, setBreakdownDimension] = useState<Dimension>('bidder')
  const buildContext = useBuildContextWithFilterEnforced('tagId', { label: searchParams.siteName, value: tagId })
  const currencySymbol = useScopeCurrency()

  const analyticsQueryByTime = useAnalyticsQueryTanstack<SiteMetricsByTime>('site.metrics.by.time', QUERY_DIMENSIONS, context, { tagId: tagId })
  const analyticsQueryByDim = useAnalyticsQueryTanstack<SiteMetricsByDim>('site.metrics.by.dim', QUERY_DIMENSIONS, context, { tagId, dimension: breakdownDimension })

  const breadcrumbs = useBreadcrumbs()
  const user = useAuthenticatedUser()
  const tabsConfig = useBreakdownTabs(user, ['bidder', 'adUnit', 'device', 'country', 'size'], { abTestPopulation: 'abtest', stackIdVersion: 'adstack' })

  const onInventoryTabChange = (_tab: SiteInventoryTabLabel) => {}

  const onBreakdownTabChange = (dimension: Dimension) => {
    setBreakdownDimension(dimension)
  }

  return (
    <AnalyticsPage
      onRefreshClick={() => {
        analyticsQueryByTime.refetch()
        analyticsQueryByDim.refetch()
      }}
      filterDimensions={FILTER_DIMENSIONS}
      queryDimensions={QUERY_DIMENSIONS}
      buildContext={buildContext}
      dashboard={DASHBOARD}
      title={breadcrumbs}
    >
      <PageContent>
        <PureSiteChartsContainer
          onInventoryTabChange={onInventoryTabChange}
          rawData={analyticsQueryByTime.data}
          onRefreshClick={() => {
            analyticsQueryByTime.refetch()
          }}
          currencySymbol={currencySymbol}
          empty={!analyticsQueryByTime.isFetching && !analyticsQueryByTime?.data?.epoch.length}
          error={!!analyticsQueryByTime.error}
          isLoading={analyticsQueryByTime.isFetching}
          timezone={context.timeRange.tz}
          currentEpoch={DateTime.now()}
        />
        <PureSiteBreakdown
          rawData={analyticsQueryByDim.data}
          onRefreshClick={() => analyticsQueryByDim.refetch()}
          onTabChange={onBreakdownTabChange}
          onRowClick={onBreakdownRowClick(breakdownDimension, setContext, !analyticsQueryByDim.data)}
          currencySymbol={currencySymbol}
          empty={!analyticsQueryByDim.isFetching && !analyticsQueryByDim.data}
          error={!!analyticsQueryByDim.error}
          isLoading={analyticsQueryByDim.isFetching}
          onSortChange={() => {}}
          tabsConfig={tabsConfig}
        />
      </PageContent>
    </AnalyticsPage>
  )
}
